import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import KosyamLogo from "@/components/KosyamLogo";
import LanguageToggle from "@/components/LanguageToggle";
import { HouseSimple, ArrowRight, Compass } from "@phosphor-icons/react";

export default function NotFound() {
  const { i18n } = useTranslation();
  const isFr = (i18n.language || "fr").startsWith("fr");
  const nav = useNavigate();

  const copy = isFr ? {
    kicker: "Erreur 404",
    title_a: "Cette page a pris",
    title_b: "un chemin de traverse.",
    desc: "L'adresse que vous cherchez n'existe pas — ou elle a été déplacée. Retournons ensemble à l'accueil pour reprendre votre parcours dans Kosyam.",
    home: "Retour à l'accueil",
    about: "À propos de Nathor Technologies",
    footer: "© 2026 Kosyam · La gestion scolaire au bout des doigts",
  } : {
    kicker: "Error 404",
    title_a: "This page took",
    title_b: "a wrong turn.",
    desc: "The address you're looking for doesn't exist — or it has moved. Let's head back home to resume your journey with Kosyam.",
    home: "Back to home",
    about: "About Nathor Technologies",
    footer: "© 2026 Kosyam · School management at your fingertips",
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-2 relative overflow-hidden">
      {/* Decorative side */}
      <div className="hidden lg:flex flex-col items-center justify-center relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20" aria-hidden
             style={{backgroundImage:'radial-gradient(rgba(255,255,255,.6) 1px, transparent 1px)', backgroundSize:'10px 10px'}} />
        <div className="relative z-10 flex flex-col items-center">
          <div className="text-[220px] font-heading font-black leading-none tracking-tighter text-accent select-none">
            404
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg overflow-hidden text-primary-foreground">
              <KosyamLogo size={48} />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">Kosyam</div>
              <div className="text-[10px] uppercase tracking-widest text-primary-foreground/70 mt-1">Nathor Technologies</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content side */}
      <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-16 relative">
        <div className="absolute top-4 right-4"><LanguageToggle /></div>

        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-widest">
            <Compass size={14} weight="duotone" /> {copy.kicker}
          </div>
          <h1 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            {copy.title_a}<br />
            <span className="text-accent">{copy.title_b}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {copy.desc}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={() => nav("/")}
              className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8"
              data-testid="btn-404-home"
            >
              <HouseSimple size={18} weight="duotone" className="mr-2" />
              {copy.home}
            </Button>
            <Link to="/a-propos">
              <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="btn-404-about">
                {copy.about} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>

          {/* Mobile 404 giant number */}
          <div className="lg:hidden mt-14 text-center">
            <div className="text-[140px] font-heading font-black leading-none text-accent/20 select-none">404</div>
          </div>
        </div>

        <div className="absolute bottom-6 left-6 right-6 text-[10px] text-muted-foreground/70 flex flex-wrap justify-between gap-2">
          <div>{copy.footer}</div>
          <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary">nathortechnologies.com</a>
        </div>
      </div>
    </div>
  );
}
